/**
 * Pure edit-state logic for the Merlin edit-proposal card's "adjust before
 * confirm" mode (review b6db3995: proposed changes must be editable).
 *
 * The card builds editable rows from the proposal payload (NOT from the
 * display-only `changes` diff), lets the CSR tweak quantities/prices or
 * exclude rows entirely, and sends the FULL adjusted set to the confirm
 * endpoint, where it is re-validated against DB truth before anything is
 * applied. Client values are never trusted server-side.
 */

export interface ProposalPayload {
  header?: Record<string, string | null> | null;
  line_updates?: Array<{
    line_item_id: string;
    quantity?: number | null;
    unit_price?: number | null;
  }>;
  // Mirrors SageLineAddPayload (quantity nullable there; validation
  // guarantees it in practice — blank renders as an empty input).
  line_adds?: Array<{
    item_code: string;
    item_description?: string | null;
    quantity?: number | null;
    unit_price?: number | null;
  }>;
  line_removes?: string[];
}

export interface EditableProposalRow {
  /** Stable row key for React lists. */
  key: string;
  kind: 'line_update' | 'line_add' | 'line_remove' | 'header';
  /** What the CSR sees ("F1210.05", "ship_via", ...). */
  label: string;
  /** The product's name ("POLYSEAL EZ 5 GAL PAIL") — review 1058222a:
   *  numbers alone aren't enough, the row must name the product. */
  description?: string;
  /** Unchecked rows are dropped from the applied set. */
  included: boolean;
  // line_update / line_add — string-typed for controlled inputs.
  quantity?: string;
  unitPrice?: string;
  /** line_add with a blank price: the CSR confirmed sending it without one
   *  (applied as an explicit $0.00 — see line_add_pricing.py). */
  noPriceConfirmed?: boolean;
  // Immutable identifiers carried through to the payload.
  lineItemId?: string;
  itemCode?: string;
  itemDescription?: string;
  // header rows
  headerField?: string;
  headerValue?: string | null;
  // line_remove rows
  removeLineItemId?: string;
}

const asInput = (v: number | null | undefined): string =>
  v === null || v === undefined ? '' : String(v);

export interface LineItemInfo {
  item_code: string;
  item_description?: string | null;
}

/** Editable rows from the proposal payload; codes + product names resolved
 *  for update/remove rows from the order's current lines. */
export function initEditRows(
  payload: ProposalPayload,
  lineItemsById: Record<string, LineItemInfo>,
): EditableProposalRow[] {
  const rows: EditableProposalRow[] = [];
  for (const [field, value] of Object.entries(payload.header ?? {})) {
    rows.push({
      key: `header:${field}`,
      kind: 'header',
      label: field,
      included: true,
      headerField: field,
      headerValue: value,
    });
  }
  for (const u of payload.line_updates ?? []) {
    const info = lineItemsById[u.line_item_id];
    rows.push({
      key: `update:${u.line_item_id}`,
      kind: 'line_update',
      label: info?.item_code ?? 'line',
      description: info?.item_description ?? undefined,
      included: true,
      lineItemId: u.line_item_id,
      quantity: asInput(u.quantity),
      unitPrice: asInput(u.unit_price),
    });
  }
  for (const [i, a] of (payload.line_adds ?? []).entries()) {
    rows.push({
      key: `add:${a.item_code}:${i}`,
      kind: 'line_add',
      label: a.item_code,
      description: a.item_description ?? undefined,
      included: true,
      itemCode: a.item_code,
      itemDescription: a.item_description ?? undefined,
      quantity: asInput(a.quantity),
      unitPrice: asInput(a.unit_price),
    });
  }
  for (const r of payload.line_removes ?? []) {
    const info = lineItemsById[r];
    rows.push({
      key: `remove:${r}`,
      kind: 'line_remove',
      label: info?.item_code ?? 'line',
      description: info?.item_description ?? undefined,
      included: true,
      removeLineItemId: r,
    });
  }
  return rows;
}

const num = (s: string | undefined): number | null => {
  const t = (s ?? '').trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

export interface BuiltEdits {
  header: Record<string, string | null> | null;
  line_updates: Array<{
    line_item_id: string;
    quantity?: number;
    unit_price?: number;
  }>;
  line_adds: Array<{
    item_code: string;
    item_description: string;
    quantity: number;
    unit_price?: number;
    no_price_confirmed?: boolean;
  }>;
  line_removes: string[];
}

export type BuildEditsResult =
  | { edits: BuiltEdits; error: null; confirmNoPrice: null }
  | { edits: null; error: string; confirmNoPrice: null }
  /** Kept add rows with no price and no confirmation yet — the card must
   *  ask "you didn't add a price for X, send it anyway?" before sending. */
  | { edits: null; error: null; confirmNoPrice: string[] };

/** Item codes of proposed adds that carry no price — the server settles
 *  prices at proposal time, so a blank here means the waterfall had nothing
 *  and a CSR must key it before confirming. */
export function unpricedAddCodes(
  payload: Pick<ProposalPayload, 'line_adds'>,
): string[] {
  return (payload.line_adds ?? [])
    .filter((a) => a.unit_price === null || a.unit_price === undefined)
    .map((a) => a.item_code);
}

/**
 * The adjusted set to send with confirm, or an error the card shows inline.
 * A row with a blank value keeps that field unproposed (qty-only / price-only
 * updates are legal); a row with a non-numeric or non-positive value where a
 * value is required is a client-side error — the server re-validates anyway.
 */
export function buildEditsPayload(rows: EditableProposalRow[]): BuildEditsResult {
  const edits: BuiltEdits = {
    header: null,
    line_updates: [],
    line_adds: [],
    line_removes: [],
  };
  let anything = false;
  const needConfirm: string[] = [];
  for (const row of rows) {
    if (!row.included) continue;
    if (row.kind === 'header' && row.headerField) {
      edits.header = {
        ...(edits.header ?? {}),
        [row.headerField]: row.headerValue ?? null,
      };
      anything = true;
    } else if (row.kind === 'line_update' && row.lineItemId) {
      const qty = num(row.quantity);
      const price = num(row.unitPrice);
      if (qty === null && price === null) {
        return {
          edits: null,
          error: `${row.label}: enter a quantity or a price, or untick the row.`,
          confirmNoPrice: null,
        };
      }
      if (qty !== null && qty <= 0) {
        return {
          edits: null,
          error: `${row.label}: quantity must be positive.`,
          confirmNoPrice: null,
        };
      }
      if (price !== null && price < 0) {
        return {
          edits: null,
          error: `${row.label}: price cannot be negative.`,
          confirmNoPrice: null,
        };
      }
      const entry: BuiltEdits['line_updates'][number] = {
        line_item_id: row.lineItemId,
      };
      if (qty !== null) entry.quantity = qty;
      if (price !== null) entry.unit_price = price;
      edits.line_updates.push(entry);
      anything = true;
    } else if (row.kind === 'line_add' && row.itemCode) {
      const qty = num(row.quantity);
      const price = num(row.unitPrice);
      if (qty === null || qty <= 0) {
        return {
          edits: null,
          error: `${row.label}: added lines need a positive quantity.`,
          confirmNoPrice: null,
        };
      }
      if (price !== null && price < 0) {
        return {
          edits: null,
          error: `${row.label}: price cannot be negative.`,
          confirmNoPrice: null,
        };
      }
      const entry: BuiltEdits['line_adds'][number] = {
        item_code: row.itemCode,
        item_description: row.itemDescription ?? row.itemCode,
        quantity: qty,
      };
      if (price !== null) {
        entry.unit_price = price;
      } else if (row.noPriceConfirmed) {
        // Sent without a price on purpose: the server applies it as an
        // explicit $0.00 (the update path runs no catalog pricing).
        entry.no_price_confirmed = true;
      } else {
        needConfirm.push(row.label);
      }
      edits.line_adds.push(entry);
      anything = true;
    } else if (row.kind === 'line_remove' && row.removeLineItemId) {
      edits.line_removes.push(row.removeLineItemId);
      anything = true;
    }
  }
  if (!anything) {
    return {
      edits: null,
      error:
        'Every change is unticked — dismiss the proposal instead of confirming nothing.',
      confirmNoPrice: null,
    };
  }
  if (needConfirm.length > 0) {
    return { edits: null, error: null, confirmNoPrice: needConfirm };
  }
  return { edits, error: null, confirmNoPrice: null };
}

/** The same rows with every blank-price add marked as confirmed by the CSR. */
export function confirmNoPriceRows(rows: EditableProposalRow[]): EditableProposalRow[] {
  return rows.map((r) =>
    r.kind === 'line_add' && (r.unitPrice ?? '').trim() === ''
      ? { ...r, noPriceConfirmed: true }
      : r,
  );
}
